import xhr from 'xhr';

import config from 'config';

export const SYNC_ALL_REQUEST = 'SYNC_ALL';
export const SYNC_REQUEST = 'SYNC';
export const STATUS_REQUEST = 'STATUS';
export const DATA_REQUEST = 'DATA';
export const OUTCOMES_REQUEST = 'OUTCOMES';

function checkData(data) {
  return (typeof data === 'object' && Boolean(data)) || typeof data === 'string';
}

function checkRequest({
  campaignId,
  testId,
  userId,
  type,
  version = 0,
  retry = {},
  status,
  data,
  callback,
} = {}) {
  if (!Number.isInteger(version)) {
    throw new Error('Invalid request version');
  }

  let result = {
    campaignId,
    testId,
    userId,
    type,
    version,
    retry,
  };

  if (type === SYNC_REQUEST || type === SYNC_ALL_REQUEST) {
    if (!status || !checkData(data)) {
      throw new Error('Invalid SYNC request data');
    }

    return {
      ...result,
      status,
      data,
    };
  }

  if (type === STATUS_REQUEST) {
    if (!status) {
      throw new Error('Invalid STATUS request data');
    }

    return {
      ...result,
      status,
    };
  }

  if (type === DATA_REQUEST) {
    if (!checkData(data)) {
      throw new Error('Invalid DATA request data');
    }

    return {
      ...result,
      data,
    };
  }

  if (type === OUTCOMES_REQUEST) {
    if (!checkData(data)) {
      throw new Error('Invalid OUTCOMES request data');
    }

    return {
      ...result,
      data,
      callback,
    };
  }

  throw new Error('Invalid request type');
}

function stringify(data) {
  return typeof data === 'string' ? data : JSON.stringify(data);
}

const schedulingModeError = () => new Error('Invalid queue scheduling mode');

function getWaitTimeExp(retryCount) {
  return Math.pow(2, retryCount) * 1000;
}

export class RequestsQueue {
  constructor({autoProcess = true, onAuthError, onLogError, onSyncAll, onProcessing} = {}) {
    this.currentTask = null;
    this.pendingTasks = [];
    this.networkRetries = 0;
    this.options = {
      autoProcess,
      onAuthError,
      onLogError,
      onSyncAll,
      onProcessing,
    };
  }

  size() {
    return this.pendingTasks.length + (this.currentTask ? 1 : 0);
  }

  _schedule(mode = 'push', request) {
    if (mode !== 'push' && mode !== 'unshift') {
      throw schedulingModeError();
    }

    const req = checkRequest(request);
    const {campaignId, testId, userId, type, version} = req;

    let found = false;
    for (let i = 0; i < this.pendingTasks.length; i += 1) {
      const task = this.pendingTasks[i];
      if (
        task.campaignId === campaignId &&
        task.testId === testId &&
        task.userId === userId &&
        task.type === type
      ) {
        if (version > task.version) {
          this.pendingTasks[i] = req;
        }
        found = true;
        break;
      }
    }
    if (!found) {
      if (mode === 'push') {
        this.pendingTasks.push(req);
      } else if (mode === 'unshift') {
        this.pendingTasks.unshift(req);
      } else {
        throw schedulingModeError();
      }
    }

    this._sort();

    this.process();
  }

  _sort() {
    this.pendingTasks.sort((a, b) => {
      if (a.userId === b.userId) {
        if (a.campaignId === b.campaignId) {
          if (a.testId === b.testId) {
            return a.version - b.version;
          }

          return a.testId - b.testId;
        }

        return a.campaignId - b.campaignId;
      }

      return a.userId - b.userId;
    });
  }

  push(request) {
    this._schedule('push', request);
  }

  unshift(request) {
    this._schedule('unshift', request);
  }

  process() {
    if (this.options.onProcessing) {
      this.options.onProcessing();
    }

    if (this.currentTask) {
      return;
    }

    if (this.pendingTasks.length === 0) {
      return;
    }

    this.currentTask = this.pendingTasks.shift();
    this._makeCall();
  }

  retry() {
    this._makeCall();
  }

  _makeCall() {
    if (!this.currentTask) {
      return;
    }

    const {campaignId, testId, userId, type, version} = this.currentTask;
    const method = 'POST';
    let apiName = 'sync';
    if (type === STATUS_REQUEST) {
      apiName = 'status';
    } else if (type === DATA_REQUEST) {
      apiName = 'data';
    }
    let url = `${config.api}/tests/${testId}/${apiName}/${campaignId}/${userId}`;
    if (type === SYNC_ALL_REQUEST) {
      url = `${config.api}/tests/checkSync/${campaignId}`;
    } else if (type === OUTCOMES_REQUEST) {
      url = `${config.api}/outcomes/${campaignId}/${testId}/${userId}`;
    }

    let data = {
      version,
    };
    if (type === SYNC_REQUEST) {
      data.status = this.currentTask.status;
      data.data = stringify(this.currentTask.data);
    } else if (type === STATUS_REQUEST) {
      data.status = this.currentTask.status;
    } else if (type === DATA_REQUEST) {
      data.data = stringify(this.currentTask.data);
    } else if (type === SYNC_ALL_REQUEST) {
      data = this.currentTask.data;
    } else if (type === OUTCOMES_REQUEST) {
      data = {
        values: this.currentTask.data,
      };
    }

    // If a sync request is made and sendBeacon API is supported, use that
    if (this.currentTask.sync && config.useBeacon && navigator && navigator.sendBeacon) {
      if (this.currentTask.type === SYNC_ALL_REQUEST) {
        this.currentTask = null;
        this.process();
        return;
      }

      this._sendBeacon({url, data});
      this.currentTask = null;
      this.process();
      return;
    }

    const queue = this;

    xhr(
      {
        method,
        url,
        body: data,
        json: true,
        sync: Boolean(this.currentTask.sync),
        timeout: config.requestsQueueTimeout,
      },
      (err, resp, body) => {
        if (err) {
          if (navigator && !navigator.onLine) {
            // Offline
            this._handleNetworkError();
            return;
          }

          if (err.code === 'ETIMEDOUT') {
            // Timeout
            this._handleNetworkError();
            return;
          }

          this._handleProbablyPermanentError(err);
          return;
        }

        const {statusCode} = resp;

        if (statusCode === 401 || statusCode === 403) {
          queue.currentTask = null;
          queue.networkRetries = 0;

          if (queue.options.onAuthError) {
            queue.options.onAuthError({statusCode, resp, body});
          }
          // Do not automatically advance the queue
          return;
        }

        if (statusCode >= 400 && statusCode < 500) {
          this._handleProbablyPermanentError(resp);
          return;
        }

        if (statusCode >= 500) {
          this._handleProbablyTemporaryError(resp);
          return;
        }

        if (statusCode >= 200 && statusCode < 400) {
          if (!body || (body && !body.status)) {
            this._handleProbablyPermanentError(resp);
            return;
          }

          // The request is successful
          if (type === SYNC_ALL_REQUEST) {
            if (this.options.onSyncAll) {
              this.options.onSyncAll(queue.currentTask, body.needPushing, campaignId);
            }
          } else if (type === OUTCOMES_REQUEST) {
            if (queue.currentTask.callback) {
              queue.currentTask.callback(body);
            }
          }
          queue.currentTask = null;
          queue.networkRetries = 0;
          if (queue.options.autoProcess) {
            queue.process();
          }
          return;
        }

        this._handleProbablyPermanentError(resp);
      }
    );
  }

  _handleNetworkError() {
    if (!this.currentTask || this.currentTask.sync) {
      return;
    }

    this.networkRetries += 1;
    let waitTime = Math.min(
      getWaitTimeExp(this.networkRetries - 1),
      config.requestsQueueNetworkMaxWaitInterval
    );
    setTimeout(() => {
      this.retry();
    }, waitTime);
  }

  _handleResponseError(type, error) {
    if (type !== 'permanent' && type !== 'temporary') {
      throw new Error('Invalid queue error type');
    }

    if (!this.currentTask || this.currentTask.sync) {
      return;
    }

    this.networkRetries = 0;

    const request = Object.assign({}, this.currentTask);

    let expectedRetryType = 'linear';
    if (type === 'temporary') {
      expectedRetryType = 'exponential';
    }
    if (request.retry && request.retry.type === expectedRetryType) {
      request.retry.attempt = Number.isInteger(request.retry.attempt)
        ? request.retry.attempt + 1
        : 1;
    } else {
      request.retry = {
        type: expectedRetryType,
        attempt: 1,
      };
    }

    if (request.retry.attempt === 1) {
      if (this.options.onLogError) {
        this.options.onLogError({
          request,
          error,
        });
      }
    } else if (request.retry.attempt > config.requestsQueueMaxRetries) {
      this.currentTask = null;
      return;
    }

    let waitTime = config.requestQueuePermanentWaitInterval;
    if (type === 'temporary') {
      waitTime = Math.min(
        getWaitTimeExp(request.retry.attempt - 1),
        config.requestsQueueTemporaryErrorMaxWaitInterval
      );
    }
    setTimeout(() => {
      this.unshift(request);
    }, waitTime);

    this.currentTask = null;
    if (this.options.autoProcess) {
      this.process();
    }
  }

  _handleProbablyPermanentError(error) {
    this._handleResponseError('permanent', error);
  }

  _handleProbablyTemporaryError(error) {
    this._handleResponseError('temporary', error);
  }

  _sendBeacon({url, data = {}}) {
    if (!url || !data) {
      return;
    }

    const fd = new FormData();
    Object.keys(data).forEach(key => {
      fd.append(key, data[key]);
    });

    navigator.sendBeacon(url, fd);
  }

  exhaust() {
    if (this.currentTask) {
      // Reschedules the current task (it may be executed twice)
      this.pendingTasks.unshift(this.currentTask);
      this.currentTask = null;
    }

    // Mark all pending tasks with the special sync attribute
    // Requests will be executed in sync mode
    // and errors will be ignored.
    // This mechanism is just a best effort to execute the pending requests.
    // No request should require its successfull execution
    this.pendingTasks.map(task => (task['sync'] = true));

    this.process();
  }

  clear() {
    this.currentTask = null;
    this.pendingTasks = [];
  }

  turnIntoLogsAndClear() {
    if (!this.options.onLogError) {
      throw new Error('onLogError must be defined!');
    }

    let tasks = this.pendingTasks.concat();
    if (this.currentTask) {
      tasks.unshift(this.currentTask);
    }

    tasks.forEach(task => {
      this.options.onLogError({
        reason: 'Logout',
        request: task,
      });
    });

    this.clear();
  }
}
